(() => {
  const QUESTIONS = [
    {
      key: 'side',
      prompt: 'Which side calls to you?',
      help: 'This guides the character and hilt styles we place first.',
      options: [
        ['light', 'Light side / Jedi'],
        ['dark', 'Dark side / Sith'],
        ['either', 'Either — surprise me']
      ]
    },
    {
      key: 'use',
      prompt: 'How will you use your saber?',
      help: 'Dueling favors durable builds. Display favors screen-inspired detail.',
      options: [
        ['dueling', 'Dueling'],
        ['display', 'Display / replica'],
        ['both', 'A bit of both']
      ]
    },
    {
      key: 'budget',
      prompt: 'What budget feels right?',
      help: 'We will favor sabers at or below this price, without hiding stronger matches.',
      options: [
        ['200', 'Under $200'],
        ['300', 'Under $300'],
        ['500', 'Under $500'],
        ['flexible', 'Flexible']
      ]
    },
    {
      key: 'characters',
      prompt: 'Whose saber style do you like?',
      help: 'Pick the closest match. You can still see every saber in your results.',
      compact: true,
      options: [
        ['any', 'Any character'],
        ['ahsoka-tano', 'Ahsoka Tano'],
        ['anakin-skywalker', 'Anakin Skywalker'],
        ['cal-kestis', 'Cal Kestis'],
        ['darth-maul', 'Darth Maul'],
        ['darth-vader', 'Darth Vader'],
        ['luke-skywalker', 'Luke Skywalker'],
        ['obi-wan-kenobi', 'Obi-Wan Kenobi'],
        ['revan', 'Revan'],
        ['yoda', 'Yoda']
      ]
    },
    {
      key: 'color',
      prompt: 'Which blade color do you want to ignite?',
      help: 'Pick a favorite, or let the other answers decide.',
      color: true,
      options: [
        ['blue', 'Blue'],
        ['green', 'Green'],
        ['red', 'Red'],
        ['purple', 'Purple'],
        ['yellow', 'Yellow'],
        ['white', 'White'],
        ['any', 'Any color']
      ]
    }
  ];

  const COLOR_VALUES = {
    blue: '#1677ff', green: '#1fbd54', red: '#ef2438', purple: '#934ad8',
    yellow: '#f2d51b', white: '#fff', any: 'linear-gradient(135deg,#1677ff,#1fbd54,#ef2438,#934ad8)'
  };

  const labelFor = (question, value) => question.options.find(([id]) => id === value)?.[1] || value;

  const initFinder = (root) => {
    if (root.dataset.ssFinderReady === 'true') return;
    root.dataset.ssFinderReady = 'true';

    const mode = root.dataset.ssFinderMode;
    const conversation = root.querySelector('[data-ss-finder-conversation]');
    const emailStep = root.querySelector('[data-ss-finder-email-step]');
    const progressLabel = root.querySelector('[data-ss-finder-progress-label]');
    const progressBar = root.querySelector('[data-ss-finder-progress-bar]');
    const back = root.querySelector('[data-ss-finder-back]');
    const form = root.querySelector('.ss-finder__email-form');
    const dialog = root.querySelector('[data-ss-finder-dialog]');
    const teaser = root.querySelector('[data-ss-finder-open]');
    const state = { step: 0, answers: {} };
    let lastFocused = null;

    try {
      const saved = JSON.parse(sessionStorage.getItem('ssSaberFinderAnswers') || '{}');
      if (saved && typeof saved === 'object') state.answers = saved;
    } catch (_) {}

    const save = () => {
      try { sessionStorage.setItem('ssSaberFinderAnswers', JSON.stringify(state.answers)); } catch (_) {}
    };

    const answerLabel = (question, answer) => {
      if (Array.isArray(answer)) return answer.map((value) => labelFor(question, value)).join(', ');
      return labelFor(question, answer);
    };

    const bubble = (kind, title, detail = '') => {
      const item = document.createElement('div');
      item.className = `ss-finder__bubble ss-finder__bubble--${kind}`;
      if (kind === 'guide') {
        item.innerHTML = `<span class="ss-finder__avatar" aria-hidden="true">SS</span><div><strong>${title}</strong>${detail ? `<p>${detail}</p>` : ''}</div>`;
      } else {
        item.innerHTML = `<div>${title}</div>`;
      }
      return item;
    };

    const finishQuestions = () => {
      state.step = QUESTIONS.length;
      conversation.hidden = true;
      emailStep.hidden = false;
      progressLabel.textContent = 'Your matches are ready';
      progressBar.style.width = '100%';
      back.disabled = false;
      emailStep.querySelector('input[type="email"]')?.focus({ preventScroll: true });
    };

    const render = () => {
      if (state.step >= QUESTIONS.length) {
        finishQuestions();
        return;
      }

      conversation.hidden = false;
      emailStep.hidden = true;
      conversation.innerHTML = '';
      progressLabel.textContent = `Question ${state.step + 1} of ${QUESTIONS.length}`;
      progressBar.style.width = `${((state.step + 1) / QUESTIONS.length) * 100}%`;
      back.disabled = state.step === 0;

      QUESTIONS.slice(0, state.step).forEach((question) => {
        conversation.appendChild(bubble('guide', question.prompt));
        conversation.appendChild(bubble('answer', answerLabel(question, state.answers[question.key])));
      });

      const question = QUESTIONS[state.step];
      conversation.appendChild(bubble('guide', question.prompt, question.help));

      const choices = document.createElement('div');
      choices.className = `ss-finder__choices${question.compact ? ' ss-finder__choices--compact' : ''}`;
      const selected = Array.isArray(state.answers[question.key]) ? state.answers[question.key] : [];

      question.options.forEach(([value, label]) => {
        const choice = document.createElement('button');
        choice.type = 'button';
        choice.className = `ss-finder__choice${selected.includes(value) || state.answers[question.key] === value ? ' is-selected' : ''}`;
        choice.dataset.ssFinderChoice = value;
        choice.setAttribute('aria-pressed', choice.classList.contains('is-selected') ? 'true' : 'false');
        const marker = question.color
          ? `<span class="ss-finder__swatch" style="--ss-choice-color:${COLOR_VALUES[value]}"></span>`
          : `<span class="ss-finder__choice-mark" aria-hidden="true">✓</span>`;
        choice.innerHTML = `${marker}<span>${label}</span>`;

        choice.addEventListener('click', () => {
          if (!question.multiple) {
            state.answers[question.key] = value;
            save();
            state.step += 1;
            window.setTimeout(render, 120);
            return;
          }

          let values = Array.isArray(state.answers[question.key]) ? [...state.answers[question.key]] : [];
          if (value === 'any') {
            values = values.includes('any') ? [] : ['any'];
          } else {
            values = values.filter((item) => item !== 'any');
            values = values.includes(value) ? values.filter((item) => item !== value) : [...values, value].slice(0, 3);
          }
          state.answers[question.key] = values;
          save();
          render();
        });
        choices.appendChild(choice);
      });

      if (question.multiple) {
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'ss-finder__choice ss-finder__continue';
        next.disabled = selected.length === 0;
        next.textContent = selected.length ? `Continue with ${selected.length === 1 && selected[0] === 'any' ? 'any character' : `${selected.length} choice${selected.length === 1 ? '' : 's'}`}` : 'Choose up to three';
        next.addEventListener('click', () => {
          if (!state.answers[question.key]?.length) return;
          state.step += 1;
          render();
        });
        choices.appendChild(next);
      }

      conversation.appendChild(choices);
      conversation.querySelector('[data-ss-finder-choice]')?.focus({ preventScroll: true });
    };

    back?.addEventListener('click', () => {
      if (state.step <= 0) return;
      state.step -= 1;
      render();
    });

    const resultsUrl = () => {
      const params = new URLSearchParams({ ss_finder: '1' });
      Object.entries(state.answers).forEach(([key, value]) => {
        if (Array.isArray(value)) params.set(key, value.join(','));
        else if (value) params.set(key, value);
      });
      return `/collections/all?${params.toString()}#ResultsList`;
    };

    form?.addEventListener('submit', (event) => {
      const error = form.querySelector('[data-ss-finder-error]');
      if (!form.reportValidity()) {
        event.preventDefault();
        return;
      }

      const tags = ['newsletter', 'saber-finder'];
      Object.entries(state.answers).forEach(([key, value]) => {
        const normalized = Array.isArray(value) ? value.join('-') : value;
        if (normalized) tags.push(`finder-${key}-${normalized}`);
      });
      form.querySelector('[data-ss-finder-tags]').value = tags.join(',');
      form.querySelector('[data-ss-finder-return-to]').value = resultsUrl();
      const submit = form.querySelector('[type="submit"]');
      submit.disabled = true;
      submit.firstChild.textContent = 'Opening your matches… ';
      error.hidden = true;
      try { localStorage.setItem('ssSaberFinderCompletedAt', String(Date.now())); } catch (_) {}
      // The native Shopify customer form saves the lead first, then honors return_to.
    });

    const openDialog = () => {
      if (!dialog) return;
      lastFocused = document.activeElement;
      dialog.hidden = false;
      teaser.hidden = true;
      document.documentElement.classList.add('ss-finder-dialog-open');
      try { sessionStorage.setItem('ssSaberFinderSeen', '1'); } catch (_) {}
      render();
      root.querySelector('[data-ss-finder-close]')?.focus({ preventScroll: true });
    };

    const closeDialog = () => {
      if (!dialog) return;
      dialog.hidden = true;
      document.documentElement.classList.remove('ss-finder-dialog-open');
      teaser.hidden = false;
      try { sessionStorage.setItem('ssSaberFinderDismissed', '1'); } catch (_) {}
      lastFocused?.focus?.({ preventScroll: true });
    };

    root.querySelectorAll('[data-ss-finder-close]').forEach((button) => button.addEventListener('click', closeDialog));
    teaser?.addEventListener('click', openDialog);

    if (mode === 'popup') {
      let completedRecently = false;
      try {
        const completedAt = Number(localStorage.getItem('ssSaberFinderCompletedAt') || 0);
        completedRecently = Date.now() - completedAt < 30 * 24 * 60 * 60 * 1000;
      } catch (_) {}
      if (!completedRecently) {
        window.setTimeout(() => { teaser.hidden = false; }, 1200);
        window.setTimeout(() => {
          let wasDismissed = false;
          try { wasDismissed = sessionStorage.getItem('ssSaberFinderDismissed') === '1'; } catch (_) {}
          if (!wasDismissed && dialog?.hidden) openDialog();
        }, 5500);
      }
    } else {
      render();
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && dialog && !dialog.hidden) closeDialog();
    });
  };

  const init = (scope = document) => scope.querySelectorAll('[data-ss-finder]').forEach(initFinder);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
